/**
 * Single source for the FAQ markup and the FAQPage JSON-LD of /support, /pricing, / (home),
 * /template, /how-it-works and /notion-habit-tracker-widget.
 *
 * To add a question: add an entry to FAQ_POOL (descriptive kebab-case id), then add its id to
 * the list of every page that should show it (SUPPORT_FAQ, PRICING_FAQ, ...). The page renders
 * the parts as markup; faqPageJsonLd() derives the structured data from the very same entries.
 */

export type FaqPart =
  | string
  | { strong: string }
  | { link: string; routerLink: string }
  | { link: string; href: string };

export interface FaqEntry {
  readonly question: string;
  readonly answer: readonly FaqPart[];
}

export const FAQ_POOL = {
  // ---- /support ----
  'support-free': {
    question: 'Is Notion Habit Heroes free?',
    answer: [
      'Yes, completely. Notion Habit Heroes is free for individuals with no time limit and no paid tier: unlimited habits, streak tracking, the Notion integration and widget, shareable streak cards, freeze days, competitions, 1-on-1 duels and Insights with all-time records are all included. No card is required. We make money from separate Teams and Coaches plans built for organizations, not from individual users.',
    ],
  },
  // shared by support, home and template (#V036)
  'safety': {
    question: 'Is my data safe? What can Notion Habit Heroes see?',
    answer: [
      "When you connect Notion, you give Notion Habit Heroes permission for the one habit database you pick. It can't see anything else in your workspace, and it never changes the values you enter. To compute your streaks and points, we store what that takes: your habit names and each day's checkboxes and numbers, plus your points, streaks and stats. While you're connected, daily data older than 400 days is deleted automatically. We use a small number of service providers for hosting, email and payments, all listed in our ",
      { link: 'Privacy Policy', routerLink: '/privacy' },
      ". Card payments go through Stripe, so card details never reach our servers. The service is operated from the EU under GDPR. You can disconnect at any time from your ",
      { link: 'Profile page', routerLink: '/profile' },
      ', or delete your account there to remove your habit data.',
    ],
  },
  'template-or-app': {
    question: 'Why use Notion Habit Heroes instead of a free Notion habit tracker template?',
    answer: [
      "A regular Notion habit tracker template gives you checkboxes, and that's enough for simple logging. What Notion formulas can't do on their own is count streaks across days. Notion Habit Heroes connects to your habit database and adds what's missing: automatic streaks for every habit, points, a widget right inside your Notion page, competitions with a leaderboard, and 1-on-1 duels. Your habits stay in Notion, and you keep checking them off there as before. It's free. Our own template is the quickest start, but it also works with a database you already have. Looking at the similarly named free \"Habit Hero\" template? ",
      {
        link: "Here's how the two compare",
        routerLink: '/blog/habit-hero-template-vs-notion-habit-heroes',
      },
      '.',
    ],
  },
  'connect-workspace': {
    question: 'How do I connect my Notion workspace?',
    answer: [
      'You do not need to copy anything by hand. Sign up (or log in) on this site and click ',
      { strong: 'Connect Notion' },
      '. On the Notion screen keep the ',
      { strong: 'Duplicate template' },
      " choice: Notion copies the Habit Heroes template into your workspace and gives Notion Habit Heroes access to the habit database inside it. Once you allow it, you're connected, and your habits are picked up the next time you open the widget, with the nightly sync at your local midnight as the safety net. Already duplicated the template yourself? Choose ",
      { strong: 'Select pages' },
      ' instead and pick your copy, so you do not end up with two.',
    ],
  },
  // #V037
  'check-connection': {
    question: 'How can I check that Notion Habit Heroes is connected?',
    answer: [
      'In Notion, open ',
      { strong: 'Settings' },
      ' and then ',
      { strong: 'Connections' },
      '. While Notion Habit Heroes is connected it is listed there, and you can see which pages it can access. On our side, your ',
      { link: 'profile page', routerLink: '/profile' },
      ' shows whether Notion is connected.',
    ],
  },
  'sync-timing': {
    question: 'When does my data sync?',
    answer: [
      'Your streaks, points and leaderboards update ',
      { strong: 'every time you open the widget or press Update' },
      ' in the widget toolbar: each of those runs a live sync that reads your Notion checkboxes and recalculates instantly. A nightly sync at midnight in your configured timezone then closes the day automatically, so nothing is lost on days you never open the widget. A self-healing pass retries any sync that failed overnight.',
    ],
  },
  'streaks-points': {
    question: 'How do streaks and points work?',
    answer: [
      'Each habit earns points when it is completed. Every habit has its own streak counting consecutive days on which that habit was completed. Completing all your habits in a single day is a "perfect day" and earns a bonus. In competitions, leaderboard points rank you against others in your group.',
    ],
  },
  'streaks-timezones': {
    question: 'How do streaks work across time zones?',
    answer: [
      "Live updates happen whenever you open the widget, in any timezone. The nightly safety-net sync runs at midnight in each user's own configured timezone, so users in different countries each have their day closed at their local midnight rather than a shared global time. A self-healing job also runs every morning to catch any sync that was missed due to a technical issue and re-runs it automatically. This means a missed nightly sync will not silently break a streak; the morning job closes the gap before the next day begins.",
    ],
  },
  // shared by support, pricing and how-it-works (#V034)
  'disconnect': {
    question: 'What happens if I disconnect Notion?',
    answer: [
      'Disconnecting takes back the permission you gave Notion Habit Heroes to read your habit database. You can do it at any time from your ',
      { link: 'Profile page', routerLink: '/profile' },
      ' with ',
      { strong: 'Disconnect Notion' },
      ". Syncing stops right away. Nothing in Notion is deleted, and your history, streaks, points and badges stay in your account. You stay in your competitions, but you don't earn new points while disconnected. To come back, connect again and choose the ",
      { strong: 'same database' },
      ": your data is still there. Days in between are not filled in later, because a sync only reads today and yesterday. Reconnect by the next day and nothing is lost. If you stay disconnected longer, your current streak starts over; your longest streak and totals stay. Choosing a different database starts your history fresh. To remove your data instead, use ",
      { strong: 'Delete Account' },
      ' on your Profile page.',
    ],
  },
  'delete-account': {
    question: 'How do I delete my account?',
    answer: [
      'Go to your ',
      { link: 'Profile page', routerLink: '/profile' },
      ' and select ',
      { strong: 'Delete Account' },
      '. This permanently removes your account, habit history, streaks, and competition data. Payment records are retained for legal compliance (10 years under German tax law).',
    ],
  },
  'manage-subscription': {
    question: 'How do I manage or cancel my subscription?',
    answer: [
      'Notion Habit Heroes no longer sells individual subscriptions, so this only applies if you have an existing legacy subscription. Billing for those is still handled through the Stripe customer portal: from your ',
      { link: 'Profile page', routerLink: '/profile' },
      ', click ',
      { strong: 'Manage Subscription' },
      ' to open the portal and cancel at any time.',
    ],
  },

  // ---- /pricing ----
  'pricing-free-really': {
    question: 'Is Notion Habit Heroes really free?',
    answer: [
      'Yes. Everything an individual uses is free with no time limit: unlimited habits, streak tracking, the Notion integration and widget, shareable streak cards, freeze days, competitions, 1-on-1 duels and Insights with all-time records. There is no paid tier for individuals.',
    ],
  },
  'pricing-business-model': {
    question: 'How do you make money, then?',
    answer: [
      'From Teams and Coaches plans built for organizations, not from individual users. Teams starts from €4 per seat per month for running challenges with a group. Coaches starts from €29 per month for accountability coaches managing up to 10 clients. Both are manual onboarding, so reach out and we will set you up.',
    ],
  },
  'pricing-credit-card': {
    question: 'Do I need a credit card?',
    answer: [
      'No. The individual plan is free forever and does not ask for payment details at any point.',
    ],
  },

  // ---- / (home) ----
  'home-what-is': {
    question: 'What is Notion Habit Heroes?',
    answer: [
      "Notion Habit Heroes is a habit tracker that connects to one Notion database you choose. It reads your daily checkbox completions every time you open your widget (with a nightly safety-net sync for days you do not) and automatically calculates streaks and points, the streak and point math a Notion formula can't do on its own. Setup takes about two minutes: connect your workspace, pick your habit database and the sync takes over. Your habit data stays in your own Notion workspace; the service keeps a copy of your habit names and daily values to compute streaks and scores. Everything is free: unlimited habits, streak tracking, a live stats widget you can embed in any Notion page, group competitions, 1-on-1 duels and leaderboards.",
    ],
  },
  'home-leave-notion': {
    question: 'Do I need to leave Notion?',
    answer: [
      'No. Notion Habit Heroes runs on top of the Notion database you already use. You keep checking off habits in Notion exactly as before; every widget load reads your completions and updates streaks, points and leaderboards on the spot, with a nightly sync as the safety net. The live widget embeds directly into any Notion page, so your stats, streaks, and current competitions show up right next to your habit tracker. The web app is there for setup, account management, and deeper stats, but day to day you can stay inside Notion.',
    ],
  },
  'home-free': {
    question: 'Is it free?',
    answer: [
      'Yes, completely free for individuals with no time limit. It includes unlimited habits, streak tracking, the Notion integration and widget, shareable streak cards, freeze days, group competitions, 1-on-1 duels, leaderboards and Insights with advanced stats. No card required. Notion Habit Heroes makes money from separate Teams and Coaches plans for organizations, not from individual users.',
    ],
  },
  'home-sync-how': {
    question: 'How does the Notion sync work?',
    answer: [
      "During setup you pick one checkbox database in your Notion workspace. Every time you open your widget, the app reads that day's completions and recalculates your streaks and points on the spot. Streak counting needs to read across many rows in date order, which Notion formulas can't do reliably, so that math runs on the server and the results are written to your stats and widget. A nightly pass at midnight in your own timezone creates the fresh journal entry for the new day and closes the day for anyone who did not open the widget. If a sync ever fails, a self-healing job catches up automatically the next morning. Disconnecting your Notion account stops all syncing immediately.",
    ],
  },

  // ---- /template ----
  'template-free': {
    question: 'Is the template free?',
    answer: [
      'Yes. The template is free, and you do not even have to copy it yourself: when you connect Notion from this site, Notion duplicates it into your workspace for you. If you only want the template without an account, use the link above to duplicate it by hand.',
    ],
  },
  'template-streaks-automatic': {
    question: 'Can Notion track streaks automatically?',
    answer: [
      'No, not with a formula alone. A Notion formula only sees its own row, so it cannot count across days on its own. Building a streak this way means either a fragile rollup formula that breaks when a day is added or removed, or an integration that reads your habit history and does the counting itself. This template pairs with the free Habit Heroes integration, which computes your streaks via the official Notion API every time you open the ',
      { link: 'widget', routerLink: '/notion-habit-tracker-widget' },
      ', with a nightly pass at your local midnight as the safety net.',
    ],
  },
  'template-account-needed': {
    question: 'Do I need a Habit Heroes account to use the template?',
    answer: [
      'You can duplicate the template and tick checkboxes without one. But the template is built for the free Habit Heroes integration: connecting adds automatic streak counting, points, the live widget, and habit management inside the widget. Solo tracking is free with no time limit.',
    ],
  },
  'template-connect-adds': {
    question: 'What does connecting to Habit Heroes actually add?',
    answer: [
      'The integration reads your daily checkbox data via the Notion API every time you open the widget and computes the streaks and points that Notion formulas cannot calculate on their own. A nightly pass at your local midnight closes the day if you never open the widget and refreshes the longer-term statistics. The results show up in the widget embedded in the template. From then on you also add, edit, and schedule your habits in the widget instead of editing database properties. Your habit entries stay in your own workspace.',
    ],
  },
  'template-competitions-free': {
    question: 'Are competitions free?',
    answer: [
      'Yes. Creating and joining group competitions, starting and accepting 1-on-1 duels and data Insights are all free for individuals, along with streak tracking, points, habit management in the widget, freeze days and the embedded widget itself.',
    ],
  },
  'template-notion-good': {
    question: 'Is Notion good for habit tracking?',
    answer: [
      "Yes, for recording what you did. A database with one row per day and one checkbox per habit works well as the daily log, and takes only a few minutes to set up. The structural limit is that Notion formulas cannot read other rows or pages, so they cannot calculate a real streak or award points on their own. That's the layer the free Habit Heroes integration adds.",
    ],
  },
  'template-how-many-habits': {
    question: 'How many habits should I track at once?',
    answer: [
      'Keep it under ten, and start with three to five, the same range our full setup guide recommends. Add more only once the ones you already have feel automatic. Go much beyond that and the daily check-in stops being a quick two-minute ritual and turns into a chore.',
    ],
  },
  'template-aesthetic-version': {
    question: 'Is there an aesthetic or minimalist version of this template?',
    answer: [
      "Not this one, it's deliberately plain: one row per day, one checkbox per habit, built to connect straight to the free integration rather than to look a particular way. If a style-focused tracker is more your thing, ",
      {
        link: 'our roundup of the best free Notion habit tracker templates',
        href: '/blog/best-notion-habit-tracker-templates',
      },
      ' covers a few aesthetic and minimalist picks worth a look.',
    ],
  },

  // ---- /how-it-works ----
  'howto-setups': {
    question: 'Which Notion setups work?',
    answer: [
      'You need a Notion database where each row represents one day and has at least one checkbox property per habit you want to track. The free ',
      { link: 'Notion habit tracker template', routerLink: '/template' },
      ' is set up exactly this way, and connecting Notion from this site copies it into your workspace for you. If you already have a daily-pages database with checkbox properties, choose Select pages during the connect and pick it instead.',
      ' With your own database you add the widget yourself: the setup page gives you your personal widget link to paste onto any Notion page.',
    ],
  },
  'howto-miss-night': {
    question: 'What happens if I miss checking habits one night?',
    answer: [
      "Missing one night does not cost you anything right away. You can still check off yesterday's habits any time today. It counts as soon as a sync runs that day: automatically when you open the Notion page with your widget, or on the spot with the Update button in the widget toolbar. Only from the day after is yesterday settled and no longer changeable. Freeze days protect a streak on a day you genuinely miss beyond that window.",
    ],
  },
  'howto-streak-updates': {
    question: 'When do streaks update?',
    answer: [
      'Your streak count updates whenever a sync runs. A sync runs every time you open the Notion page with your widget, or on the spot when you click Update in the widget toolbar, so a check-off shows up in your streaks and points instantly. Independently, a nightly sync runs at midnight in your configured timezone to close the day even if you never opened the widget. A self-healing check also retries any sync that failed overnight, so by the time you start your day your streaks reflect the previous night.',
    ],
  },
  'howto-existing-database': {
    question: 'Does it work with an existing habit database, or only the template?',
    answer: [
      'Both. During setup you can connect any existing Notion database that has daily pages with checkbox properties. See the ',
      { link: 'template page', routerLink: '/template' },
      ' for the exact schema the integration expects, so you can compare it against your own setup. For most users, letting the connect step copy the free template is the fastest starting point.',
      ' With your own database you add the widget yourself: the setup page gives you your personal widget link to paste onto any Notion page.',
    ],
  },

  // ---- /notion-habit-tracker-widget (#L055) ----
  'widget-free': {
    question: 'Is the Notion habit tracker widget free?',
    answer: [
      'Yes. The widget and everything in it are free for individuals: streaks, the progress bar, Insights, competitions and duels. There is no paid tier for individuals, no trial that runs out and no card to enter.',
    ],
  },
  'widget-formulas': {
    question: 'Do I need Notion formulas for the widget?',
    answer: [
      'No. Your database only needs one row per day with a checkbox for each habit. Notion Habit Heroes reads those checkboxes through the Notion API and computes streaks, completion rates and points on its own server, so there is no formula to write or maintain.',
    ],
  },
  'widget-existing-database': {
    question: 'Does the widget work with my own habit tracker?',
    answer: [
      'Yes, as long as each day is one row with a checkbox per habit. Choose Select pages when you connect and pick your database. See ',
      { link: 'which templates work as they are', routerLink: '/works-with' },
      '. Starting fresh? Keep Duplicate template instead: the template comes with the widget already embedded.',
    ],
  },
  'widget-link-private': {
    question: 'Is my widget link safe to share?',
    answer: [
      'No, keep it to yourself. The link carries a personal token. Anyone who has it can use the widget as you, for example to edit your habits. Keep the widget on a private Notion page and do not publish that page to the web.',
    ],
  },
  'widget-friends-see': {
    question: 'Can my friends see my widget?',
    answer: [
      'They cannot open your widget, which only works with your personal link. When you are in a competition together, they see you on its leaderboard with your points and completion rate. In a duel, your opponent sees your completion rate and perfect days next to theirs.',
    ],
  },
} as const satisfies Record<string, FaqEntry>;

export type FaqId = keyof typeof FAQ_POOL;

export const SUPPORT_FAQ: readonly FaqId[] = [
  'support-free',
  'safety',
  'template-or-app',
  'connect-workspace',
  'check-connection',
  'sync-timing',
  'streaks-points',
  'streaks-timezones',
  'disconnect',
  'delete-account',
  'manage-subscription',
];

export const PRICING_FAQ: readonly FaqId[] = [
  'pricing-free-really',
  'pricing-business-model',
  'pricing-credit-card',
  'disconnect',
];

export const HOME_FAQ: readonly FaqId[] = [
  'home-what-is',
  'home-leave-notion',
  'home-free',
  'home-sync-how',
  'safety',
];

export const TEMPLATE_FAQ: readonly FaqId[] = [
  'template-free',
  'template-streaks-automatic',
  'template-account-needed',
  'template-connect-adds',
  'safety',
  'template-competitions-free',
  'template-notion-good',
  'template-how-many-habits',
  'template-aesthetic-version',
];

export const HOW_IT_WORKS_FAQ: readonly FaqId[] = [
  'howto-setups',
  'howto-miss-night',
  'howto-streak-updates',
  'disconnect',
  'howto-existing-database',
];

export const WIDGET_FAQ: readonly FaqId[] = [
  'widget-free',
  'widget-formulas',
  'widget-existing-database',
  'widget-link-private',
  'widget-friends-see',
];

/** Visible text of an answer (strong and link text, no URLs), whitespace collapsed and trimmed. */
export function faqPlainText(parts: readonly FaqPart[]): string {
  return parts
    .map((part) => (typeof part === 'string' ? part : 'strong' in part ? part.strong : part.link))
    .join('')
    .replace(/\s+/g, ' ')
    .trim();
}

/** FAQPage JSON-LD generated from the same entries the page renders. */
export function faqPageJsonLd(ids: readonly FaqId[]): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: ids.map((id) => {
      const entry: FaqEntry = FAQ_POOL[id];
      return {
        '@type': 'Question',
        name: entry.question,
        acceptedAnswer: { '@type': 'Answer', text: faqPlainText(entry.answer) },
      };
    }),
  };
}

export type FaqPartKind = 'text' | 'strong' | 'routerLink' | 'href';

/** Template helper: Angular templates cannot narrow the FaqPart union with `in`. */
export function faqPartKind(part: FaqPart): FaqPartKind {
  if (typeof part === 'string') {
    return 'text';
  }
  if ('strong' in part) {
    return 'strong';
  }
  return 'routerLink' in part ? 'routerLink' : 'href';
}

export function asStrong(part: FaqPart): { strong: string } {
  return part as { strong: string };
}

export function asRouterLink(part: FaqPart): { link: string; routerLink: string } {
  return part as { link: string; routerLink: string };
}

export function asHref(part: FaqPart): { link: string; href: string } {
  return part as { link: string; href: string };
}

export interface FaqItem extends FaqEntry {
  readonly id: FaqId;
}

/** Entries of a page list, in list order, typed for @for in templates. */
export function faqItems(ids: readonly FaqId[]): readonly FaqItem[] {
  return ids.map((id) => {
    const entry: FaqEntry = FAQ_POOL[id];
    return { id, question: entry.question, answer: entry.answer };
  });
}
